import { randomChars } from "../shared/http";

interface ParticipantKeys {
  pid: string | null;
  editToken: string | null;
}

export interface Identity extends ParticipantKeys {
  adminToken: string | null;
}

const memory = new Map<string, ParticipantKeys>();
const adminMemory = new Map<string, string>();

function key(eventId: string): string {
  return `overlap:${eventId}`;
}

function adminKey(eventId: string): string {
  return `${key(eventId)}:admin`;
}

function empty(): Identity {
  return { pid: null, editToken: null, adminToken: null };
}

function parseIdent(raw: string): Identity {
  const data: unknown = JSON.parse(raw);
  if (typeof data !== "object" || data === null) return empty();
  const pid = "pid" in data && typeof data.pid === "string" ? data.pid
    : "participantId" in data && typeof data.participantId === "string" ? data.participantId : null;
  const editToken = "editToken" in data && typeof data.editToken === "string" ? data.editToken : null;
  const adminToken = "adminToken" in data && typeof data.adminToken === "string" ? data.adminToken : null;
  return { pid, editToken, adminToken };
}

function loadAdmin(eventId: string): string | null {
  try {
    const stored = localStorage.getItem(adminKey(eventId));
    if (stored !== null) return stored;
    const own = adminMemory.get(eventId);
    if (own !== undefined) return own;
    const raw = localStorage.getItem(key(eventId));
    const legacy = raw ? parseIdent(raw).adminToken : null;
    if (legacy !== null) {
      storeAdmin(eventId, legacy);
      return legacy;
    }
  } catch {
    /* A page that could not persist its organizer key still owns its in-memory copy. */
  }
  return adminMemory.get(eventId) ?? null;
}

export function loadIdentity(eventId: string): Identity {
  let ident = memory.get(eventId);
  if (!ident) {
    try {
      const raw = localStorage.getItem(key(eventId));
      if (raw) {
        const stored = parseIdent(raw);
        ident = { pid: stored.pid, editToken: stored.editToken };
        memory.set(eventId, ident);
      }
    } catch {
      /* Joining remains available when storage is blocked. */
    }
  }
  return { pid: ident?.pid ?? null, editToken: ident?.editToken ?? null, adminToken: loadAdmin(eventId) };
}

export function saveIdentity(eventId: string, ident: ParticipantKeys): boolean {
  const copy: ParticipantKeys = { pid: ident.pid, editToken: ident.editToken };
  memory.set(eventId, copy);
  const payload = JSON.stringify(copy);
  try {
    localStorage.setItem(key(eventId), payload);
    return localStorage.getItem(key(eventId)) === payload;
  } catch {
    return false;
  }
}

export function storeAdmin(eventId: string, adminToken: string): boolean {
  adminMemory.set(eventId, adminToken);
  try {
    localStorage.setItem(adminKey(eventId), adminToken);
    return localStorage.getItem(adminKey(eventId)) === adminToken;
  } catch {
    return false;
  }
}

/** Always mints a fresh pid and edit token and discards whatever was stored before, so the join
 * button works the same way every time it is pressed: including the one time it matters most,
 * re-joining after the organizer removed this identity, where the old edit token is dead on the
 * server and the old pid could otherwise be mistaken for "removed again" the moment a state that
 * predates the rejoin arrives. The admin token, a separate concern, is preserved. */
export function beginJoinKeys(eventId: string): { pid: string; editToken: string; persisted: boolean } {
  const ident = loadIdentity(eventId);
  const pid = randomChars(10);
  const editToken = randomChars(24);
  const persisted = saveIdentity(eventId, { ...ident, pid, editToken });
  return { pid, editToken, persisted };
}

export function clearJoinKeys(eventId: string): boolean {
  return saveIdentity(eventId, { pid: null, editToken: null });
}

/** Reads a well-formed `#admin=` hash without touching history. Split from stripping it so the
 * caller can decide, from the outcome of checking the token, whether the hash should survive (a
 * transient failure) or go (a definite answer). */
export function readAdminHash(): string | null {
  try {
    const m = location.hash.match(/^#admin=([A-Za-z0-9_-]{24,64})$/);
    return m && m[1] ? m[1] : null;
  } catch {
    return null;
  }
}

export function stripAdminHash(): void {
  try {
    history.replaceState(null, "", location.pathname);
  } catch {
    /* ignore */
  }
}
